import classNames from 'classnames';
import { Link } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { AirtableStatus } from 'gatsby-theme-hem-store/src/constants';
import { Text, TextColor, TextVariant } from 'hem-ui';
import React from 'react';

import { StarRating } from '../StarRating';
import {
  cite,
  content,
  placementGrid,
  placementRichtext,
  root,
  title,
  variant,
} from './RichTextReview.module.scss';

type Props = Queries.ProductReviewFragment & {
  placement: 'grid' | 'richtext';
};

const ReviewForVariant = ({
  slug,
  productName,
  variantName,
  gatsbyImageData,
  discontinued,
}: {
  slug: string | undefined;
  productName: string;
  variantName: string;
  gatsbyImageData: IGatsbyImageData | undefined;
  discontinued: boolean;
}) => {
  const content = (
    <>
      {gatsbyImageData ? <GatsbyImage alt="" image={gatsbyImageData} /> : null}
      <Text tag="p" variant={TextVariant.TITLE_X_SMALL}>
        {productName} <br />
        <Text variant={TextVariant.BODY_X_SMALL}>
          {variantName} {discontinued ? '(Discontinued)' : null}
        </Text>
      </Text>
    </>
  );

  if (discontinued || !slug) {
    return <div className={variant}>{content}</div>;
  }

  return (
    <Link to={slug} className={variant}>
      {content}
    </Link>
  );
};

export const RichTextReview: React.FC<Props> = (review): JSX.Element => {
  return (
    <div
      className={classNames(root, {
        [placementGrid]: review.placement === 'grid',
        [placementRichtext]: review.placement === 'richtext',
      })}
    >
      <blockquote>
        <Text tag="p" className={title} variant={TextVariant.TITLE}>
          {review.title}
        </Text>

        <Text
          tag="p"
          className={content}
          variant={TextVariant.BODY_SMALL}
          color={TextColor.GRAY_DARK}
          dangerouslySetInnerHTML={{
            __html: review.content.childMarkdownRemark.rawMarkdownBody,
          }}
        />

        <StarRating scores={[review.score]} showNumberOfReviews={false} />

        <Text tag="cite" className={cite} variant={TextVariant.TITLE_X_SMALL}>
          {review.name}
          <Text variant={TextVariant.BODY_X_SMALL} color={TextColor.GRAY_DARK}>
            {review.yotpoCreatedAt}
            {review.verifiedBuyer ? (
              <>
                <br />
                Verified buyer
              </>
            ) : null}
          </Text>
        </Text>
      </blockquote>

      {!!review?.variant?.airtable && (
        <ReviewForVariant
          slug={review.variant.slug}
          productName={review.variant.productName}
          variantName={review.variant.variantName}
          gatsbyImageData={review.variant.thumbnailImage.gatsbyImageData}
          discontinued={
            review.variant.airtable?.status === AirtableStatus.INACTIVE
          }
        />
      )}
    </div>
  );
};
